import React, { useState, memo } from 'react';
import OnboardingStep from './OnboardingStep';
import { useTheme } from './ThemeContext';

const PlayerOnboardingFlow = memo(({ onComplete, onSkip, creators }) => {
  const { isDarkMode } = useTheme();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <OnboardingStep
            step={1}
            totalSteps={totalSteps}
            onNext={handleNext}
            onBack={handleBack}
            onSkip={onSkip}
            showBack={false}
          >
            <div className="text-center">
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Welcome to TheSubGames!
              </h2>
              <p className={`text-lg md:text-xl mb-6 ${
                isDarkMode ? 'text-white/80' : 'text-gray-700'
              }`}>
                Play mini-games. Support creators. Everyone wins.
              </p>
              <div className={`${
                isDarkMode ? 'bg-white/10' : 'bg-blue-50'
              } rounded-xl p-6 text-left`}>
                <h3 className={`text-xl font-bold mb-3 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  How it works:
                </h3>
                <ul className={`space-y-2 ${
                  isDarkMode ? 'text-white/70' : 'text-gray-700'
                }`}>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">1️⃣</span>
                    <span>Choose a content creator to support</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">2️⃣</span>
                    <span>Play games to earn them points</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">3️⃣</span>
                    <span>Highest scoring creator wins daily promotion</span>
                  </li>
                </ul>
              </div>
            </div>
          </OnboardingStep>
        );

      case 2:
        return (
          <OnboardingStep
            step={2}
            totalSteps={totalSteps}
            onNext={handleNext}
            onBack={handleBack}
            onSkip={onSkip}
          >
            <div className="text-center">
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Choose Your Creator
              </h2>
              <p className={`text-lg mb-6 ${
                isDarkMode ? 'text-white/80' : 'text-gray-700'
              }`}>
                Pick a content creator to support. Your game points help them win!
              </p>
              
              {/* Full creator gallery mockup */}
              <div className={`${
                isDarkMode ? 'bg-white/5' : 'bg-gray-50'
              } rounded-xl p-4 border ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                {/* Search bar */}
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Search creators..."
                    disabled
                    className={`w-full px-4 py-2 rounded-lg ${
                      isDarkMode ? 'bg-white/10 text-white/50' : 'bg-white text-gray-400'
                    } border ${isDarkMode ? 'border-white/20' : 'border-gray-300'}`}
                  />
                </div>
                
                {/* Category filters */}
                <div className="flex gap-2 mb-4 overflow-x-auto">
                  {['All', 'Gaming', 'Tech', 'Entertainment', 'Education'].map((cat, i) => (
                    <button
                      key={cat}
                      disabled
                      className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                        i === 0
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                          : isDarkMode ? 'bg-white/10 text-white/70' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                
                {/* Creator grid */}
                <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                  {[
                    { name: 'GamingWithAlex', points: 2847, subs: 156, tags: ['Gaming', 'FPS'] },
                    { name: 'SarahPlaysGames', points: 2603, subs: 128, tags: ['Gaming', 'Variety'] },
                    { name: 'TechWithMike', points: 2451, subs: 118, tags: ['Tech', 'Reviews'] },
                    { name: 'EmmaGaming', points: 2287, subs: 105, tags: ['Gaming', 'Comedy'] },
                  ].map((creator, i) => (
                    <div
                      key={i}
                      className={`${
                        isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'
                      } ${i === 0 ? 'border-2 border-green-500' : 'border'} rounded-lg p-3`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex-shrink-0"></div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs font-semibold truncate ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {creator.name}
                          </div>
                          <div className={`text-[10px] ${
                            isDarkMode ? 'text-white/50' : 'text-gray-500'
                          }`}>
                            YouTube
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between text-[10px] mb-2">
                        <span className={isDarkMode ? 'text-white/70' : 'text-gray-600'}>
                          <span className="font-bold">{creator.points.toLocaleString()}</span> POINTS
                        </span>
                        <span className={isDarkMode ? 'text-white/70' : 'text-gray-600'}>
                          <span className="font-bold">{creator.subs}</span> SUPPORTERS
                        </span>
                      </div>
                      <div className="flex gap-1 mb-2">
                        {creator.tags.map(tag => (
                          <span key={tag} className={`text-[9px] px-1.5 py-0.5 rounded ${
                            isDarkMode ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                      <button className={`w-full py-1.5 rounded text-xs font-semibold ${
                        i === 0
                          ? 'bg-green-500 text-white'
                          : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                      }`}>
                        {i === 0 ? 'SELECTED ✓' : 'Select Creator'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </OnboardingStep>
        );

      case 3:
        return (
          <OnboardingStep
            step={3}
            totalSteps={totalSteps}
            onNext={handleNext}
            onBack={handleBack}
            onSkip={onSkip}
          >
            <div className="text-center">
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Play to Earn Points!
              </h2>
              <p className={`text-lg mb-6 ${
                isDarkMode ? 'text-white/80' : 'text-gray-700'
              }`}>
                Every game you play earns points for your chosen creator
              </p>
              
              <div className={`${
                isDarkMode ? 'bg-white/10' : 'bg-blue-50'
              } rounded-xl p-6 text-left space-y-4`}>
                <div className="flex items-center gap-4">
                  <div className="text-4xl">🎮</div>
                  <div>
                    <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      7 Different Games
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-700'}`}>
                      Block Blast, Whack-a-Mole, Color Match, and more!
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-4xl">⚡</div>
                  <div>
                    <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Earn Points
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-700'}`}>
                      Every point you score = 1 point for your creator. Some games are worth more!
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-4xl">🔄</div>
                  <div>
                    <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Switch Anytime
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-700'}`}>
                      Choose a different creator to support each day
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-4xl">🏆</div>
                  <div>
                    <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Help Them Win
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-700'}`}>
                      The more you play, the better their chances!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </OnboardingStep>
        );

      case 4:
        return (
          <OnboardingStep
            step={4}
            totalSteps={totalSteps}
            onNext={handleNext}
            onBack={handleBack}
            onSkip={onSkip}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">📊</div>
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Check the Leaderboard
              </h2>
              <p className={`text-lg mb-6 ${
                isDarkMode ? 'text-white/80' : 'text-gray-700'
              }`}>
                See how your creator is doing in real-time
              </p>
              
              <div className={`${
                isDarkMode ? 'bg-white/10' : 'bg-gray-50'
              } rounded-xl p-6 space-y-4`}>
                {/* Mock leaderboard */}
                <div className={`${
                  isDarkMode ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-400/40' : 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-400'
                } border-2 rounded-lg p-4 flex items-center gap-4`}>
                  <div className="text-3xl">👑</div>
                  <div className="flex-1 text-left">
                    <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Your Creator
                    </div>
                    <div className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-700'}`}>
                      1,234 points
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-yellow-500">#1</div>
                </div>
                
                {/* Timer mockup */}
                <div className="text-center py-4">
                  <div className={`inline-block px-6 py-3 rounded-xl font-mono text-2xl font-bold ${
                    isDarkMode ? 'bg-gray-800 text-white' : 'bg-gray-900 text-white'
                  }`} style={{
                    boxShadow: '0 0 40px rgba(102, 126, 234, 0.6), 0 0 80px rgba(236, 72, 153, 0.4)'
                  }}>
                    12:34:56
                  </div>
                  <p className={`text-sm mt-2 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>
                    Next Winner In
                  </p>
                </div>
                
                <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-700'}`}>
                  Winners announced daily at <strong>6pm CST</strong>
                </p>
              </div>
            </div>
          </OnboardingStep>
        );

      case 5:
        return (
          <OnboardingStep
            step={5}
            totalSteps={totalSteps}
            onNext={handleNext}
            onBack={handleBack}
            onSkip={onSkip}
            nextLabel="Start Playing!"
            canSkip={false}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">🚀</div>
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                You're Ready!
              </h2>
              <p className={`text-lg mb-6 ${
                isDarkMode ? 'text-white/80' : 'text-gray-700'
              }`}>
                Time to start playing and help your creator win
              </p>
              
              <div className={`${
                isDarkMode ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-400/40' : 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-300'
              } border-2 rounded-xl p-6 space-y-4`}>
                <div className="text-left space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm">✓</div>
                    <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>Choose a creator on the Leaderboard tab</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm">✓</div>
                    <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>Play games on the Games tab</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm">✓</div>
                    <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>Watch them climb the leaderboard!</span>
                  </div>
                </div>
                
                <div className={`${
                  isDarkMode ? 'bg-white/10' : 'bg-white'
                } rounded-lg p-4 mt-4`}>
                  <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-700'}`}>
                    💡 <strong>Pro tip:</strong> Play multiple games to maximize points for your creator!
                  </p>
                </div>
              </div>
            </div>
          </OnboardingStep>
        );

      default:
        return null;
    }
  };

  return renderStepContent();
});

export default PlayerOnboardingFlow;
